import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Group } from '@visx/group';
import { AxisTop, AxisLeft, AxisBottom } from '@visx/axis';
import { scaleLinear, scaleBand } from '@visx/scale';
import { Line } from '@visx/shape';
import { useTooltip } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import StackedChartImage from '../../components/ChartsImages/StackedChartImage';
import { GridRows } from '@visx/grid';
import ChartTooltip from '../../components/ChartTooltip';
import {
  getColors,
  MAP_CHART_FIELDS_THRESHOLD,
  METRICS,
  useWindowSize
} from '../../utils';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import styled from 'styled-components';
import uniqid from 'uniqid';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import StackedBar from '../../components/StackedBar';

const StyledStackedChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  padding: 10px;
`;

type StackedChartProps = {
  margin: any;
  data: any[];
  emptyDataMessage?: string;
  color: string | string[];
  limit: any;
  showItemsPreview?: boolean;
  tickProps?: any;
  axesProps: any;
  label: string;
  displayValue?: boolean;
  tooltipContent: (_d) => any;
};

let tooltipTimeout: number;
const defaultMargin = { top: 50, right: 50, bottom: 50, left: 100 };

const StackedChart: FC<StackedChartProps> = (props) => {
  const {
    data,
    margin = defaultMargin,
    emptyDataMessage,
    color,
    limit,
    showItemsPreview,
    tickProps,
    axesProps = {},
    label,
    displayValue = true,
    tooltipContent
  } = props;
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const isEmpty = !data || data.length === 0;
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();

  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  const filteredData = useMemo(() => {
    if (isEmpty) {
      return [];
    }
    return limit !== 'auto' ? data.slice(0, limit) : data;
  }, [isEmpty, data, limit]);

  const getGroupContent = useCallback((group, groups, index) => {
    const options = groups.tickLabelProps(group.value);
    const props = {
      ...options,
      x: group.to.x,
      y: 10 + group.to.y,
      index
    };

    return groups.tickComponent(props);
  }, []);

  const { xAxis, xAxisPosition, zAxis, showYAxis } = axesProps;

  const xValues =
    (filteredData && filteredData.map((item: any) => item[xAxis])) || [];
  const zValues =
    (filteredData && filteredData.map((item: any) => item[zAxis])) || [];

  const minMaxValues = useMemo(() => {
    return {
      xMin: Math.min(...xValues),
      xMax: Math.max(...xValues),
      zMin: zAxis ? Math.min(...zValues) : Math.min(...xValues),
      zMax: zAxis ? Math.max(...zValues) : Math.max(...xValues)
    };
  }, [zAxis, xValues, zValues]);

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, index, color = '#269F49') => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(svgRef.current, event);
      showTooltip({
        tooltipData: {
          ...filteredData[index],
          color
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, filteredData]
  );

  const colorScale = useMemo(
    () =>
      scaleLinear<string>({
        domain: [
          0,
          isEmpty ? 0 : filteredData.length / 2,
          isEmpty ? 0 : filteredData.length
        ],
        range: getColors()
      }),
    [filteredData, isEmpty]
  );

  const getTickComponent = useCallback(
    (d) => {
      const index = filteredData.findIndex((i) => i.id === d.id);
      return (
        <StackedChartImage
          key={`stacked-${chartId.current}-category-${d.id}`}
          d={{ ...d, index }}
          chartId={chartId.current}
          color={d.color || colorScale(index)}
          showItemsPreview={showItemsPreview}
          setMinFontSize={filteredData && filteredData.length > 5}
          limitText={tickProps && tickProps.limit}
          textWidth={tickProps && tickProps.width}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
      );
    },
    [
      filteredData,
      showItemsPreview,
      tickProps,
      chartId.current,
      handleMouseMove,
      handleMouseLeave,
      colorScale
    ]
  );

  const xzDiff = Math.max(
    minMaxValues.xMax - minMaxValues.xMin,
    minMaxValues.zMax - minMaxValues.zMin,
    0
  );
  const xzMin = Math.min(minMaxValues.xMin, minMaxValues.zMin);
  const xzMax = Math.max(minMaxValues.xMax, minMaxValues.zMax);

  const threshold_x = MAP_CHART_FIELDS_THRESHOLD[xAxis] || 0;

  const offsetXZMin =
    threshold_x > 0 && xzMin >= threshold_x
      ? threshold_x
      : (xzMin - xzDiff) / 10;
  const offsetXZMax =
    threshold_x > 0 && xzMax + xzDiff / 10 < threshold_x
      ? threshold_x
      : xzMax + xzDiff / 10;
  const xDomain = useMemo(
    () => [
      METRICS.AFFINITY !== xAxis || offsetXZMin < 0 ? 0 : offsetXZMin,
      offsetXZMax
    ],
    [xAxis, offsetXZMin, offsetXZMax]
  );

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: xDomain,
        range: [0, xzMax]
      }),
    [xDomain, xzMax]
  );

  const yScale = useMemo(
    () =>
      scaleBand({
        domain: (filteredData && filteredData.map((item) => item.id)) || [],
        padding: 0.7
      }),
    [filteredData]
  );

  const getTickImage = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.pic) || '';
    },
    [filteredData]
  );

  const getTickLabel = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.label) || '';
    },
    [filteredData]
  );

  const xTicks = useMemo(() => {
    let min = xzDiff < 1 ? 5 : xzDiff + 4;
    if (xAxis !== METRICS.AFFINITY) {
      min = xzDiff <= 0.01 ? 1 : xzDiff * 100;
    }

    if (width < 520) {
      return Math.min(min, 2);
    }
    return Math.min(min, 10);
  }, [xzDiff, width, xAxis]);

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  xScale.rangeRound([0, xMax]);
  yScale.rangeRound([yMax, 0]);

  const horizontalAxis = useMemo(() => {
    if (!xAxisPosition || xAxisPosition === 'top') {
      return (
        <AxisTop
          label={label}
          top={0}
          scale={xScale}
          numTicks={xTicks}
          stroke='#9493A6'
          tickStroke='#9493A6'
          tickFormat={(d: any) => tickProps && tickProps.format(d, xAxis)}
          tickLabelProps={() => ({
            fill: '#9493A6',
            fontSize: 11,
            textAnchor: 'middle',
            fontWeight: 'bold',
            dy: -2
          })}
          labelProps={{
            fill: 'black',
            textAnchor: 'middle',
            fontSize: 14,
            fontWeight: 'bold',
            dy: -5
          }}
        />
      );
    }
    return (
      <AxisBottom
        label={label}
        top={yMax}
        scale={xScale}
        numTicks={xTicks}
        stroke='#9493A6'
        tickStroke='#9493A6'
        tickFormat={(d: any) => tickProps && tickProps.format(d, xAxis)}
        tickLabelProps={() => ({
          fill: '#9493A6',
          fontSize: 11,
          textAnchor: 'middle',
          fontWeight: 'bold'
        })}
        labelProps={{
          fill: 'black',
          textAnchor: 'middle',
          fontSize: 14,
          fontWeight: 'bold'
        }}
      />
    );
  }, [xAxisPosition, label, yMax, xScale, xTicks, xAxis]);

  if (isEmpty) {
    return (
      <StyledStackedChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledStackedChartWrapper>
    );
  }

  return (
    <StyledStackedChartWrapper ref={chartRef}>
      <svg ref={svgRef} width={width} height={height}>
        <rect width={width} height={height} fill='#ffffff' rx={14} />
        <GridRows
          top={margin.top}
          left={margin.left}
          scale={yScale}
          width={xMax}
          height={yMax}
          offset={10}
        />
        <Group
          top={margin.top}
          left={margin.left}
          key={`group-${chartId.current}`}
        >
          {filteredData.map((d, index) => (
            <StackedBar
              key={index}
              chartId={chartId}
              xAxis={xAxis}
              zAxis={zAxis}
              item={d}
              index={index}
              color={color}
              xScale={xScale}
              yScale={yScale}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              displayValue={displayValue}
              totalCount={isEmpty ? 0 : filteredData.length}
            />
          ))}
          <Line
            from={{ x: xScale(threshold_x), y: 0 }}
            to={{
              x: xScale(threshold_x),
              y: yMax
            }}
            stroke='#9493A6'
            strokeWidth='1'
            strokeOpacity={0.5}
          />
          {showYAxis && (
            <AxisLeft
              scale={yScale}
              tickValues={yScale.domain()}
              tickLabelProps={(d) => ({
                fontSize: '11px',
                pic: getTickImage(d),
                id: d,
                label: getTickLabel(d)
              })}
              tickComponent={(d) => getTickComponent(d)}
            >
              {(axisGroups) => (
                <React.Fragment>
                  {axisGroups.ticks.map((group, index) => {
                    return (
                      <Group
                        key={`stacked-${chartId.current}-axis-group-${index}`}
                      >
                        {getGroupContent(group, axisGroups, index)}
                      </Group>
                    );
                  })}
                </React.Fragment>
              )}
            </AxisLeft>
          )}
          {horizontalAxis}
        </Group>
      </svg>

      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          {tooltipContent && tooltipContent(tooltipData)}
        </ChartTooltip>
      )}
    </StyledStackedChartWrapper>
  );
};
export default StackedChart;
